import type { ReactNode } from 'react';
import { useRouteLoaderData } from 'react-router-dom';

import type { RootLoaderData } from '../App';
import { NotAuthorizedPage } from '../pages/NotAuthorizedPage';

interface PermissionGuardProps {
  children: ReactNode;
  /**
   * The permission key to check. Currently enforced as an admin/owner role
   * requirement. Fine-grained UEAC enforcement is handled by useElementAccess
   * within each page; this guard is for route-level access only.
   *
   * Supported values that map to admin-or-owner check:
   *   'roles:write' | 'settings:api_keys:read' | 'settings:addons:read'
   */
  permission: string;
}

/** Route-level guard: shows NotAuthorizedPage if the current user lacks the permission. */
export function PermissionGuard({ children, permission: _permission }: PermissionGuardProps) {
  const data = useRouteLoaderData('root') as RootLoaderData | undefined;

  if (!data) return null;

  const role = data.membership?.role_name;
  const isAdminOrOwner = role === 'admin' || role === 'owner';

  if (!isAdminOrOwner) {
    return <NotAuthorizedPage />;
  }

  return <>{children}</>;
}
